import { authenticatedAPI } from '../utils/authApi';

class SocialApiService {
  constructor() {
    this.apiName = 'devices'; // Using the same API name as other services for now
  }

  async getActivityFeed(limit = 20, offset = 0) {
    try {
      const response = await authenticatedAPI.get(this.apiName, '/social/feed', {
        queryStringParameters: { 
          limit: limit.toString(), 
          offset: offset.toString() 
        }
      });
      return response;
    } catch (error) {
      console.error('Error fetching activity feed:', error);
      // Return mock activity feed for demo purposes
      return {
        activities: [
          {
            id: 'activity1',
            user: {
              id: 'friend1',
              name: 'John Doe',
              avatar: null
            },
            type: 'steps',
            description: 'Reached 15,000 steps today!',
            timestamp: '2024-01-20T14:30:00Z',
            data: {
              steps: 15000,
              goal: 10000
            },
            likes: 5,
            comments: 2,
            isLiked: false
          },
          {
            id: 'activity2',
            user: {
              id: 'friend2',
              name: 'Jane Smith',
              avatar: null
            },
            type: 'challenge',
            description: 'Completed the "Weekly Challenge" with 70,000 steps!',
            timestamp: '2024-01-20T12:15:00Z',
            data: {
              challengeName: 'Weekly Challenge',
              totalSteps: 70000,
              rank: 3
            },
            likes: 12,
            comments: 4,
            isLiked: true
          },
          {
            id: 'activity3',
            user: {
              id: 'friend3',
              name: 'Mike Johnson',
              avatar: null
            },
            type: 'achievement',
            description: 'Unlocked "Streak Master" achievement - 30 days in a row!',
            timestamp: '2024-01-20T09:45:00Z',
            data: {
              achievement: 'Streak Master',
              streak: 30
            },
            likes: 8,
            comments: 1,
            isLiked: false
          }
        ],
        hasMore: true
      };
    }
  }

  async shareActivity(activityData) {
    try {
      const response = await authenticatedAPI.post(this.apiName, '/social/activity', {
        body: activityData
      });
      return response;
    } catch (error) {
      console.error('Error sharing activity:', error);
      // Simulate success for demo purposes
      return { 
        success: true, 
        activityId: 'new_activity_' + Date.now(),
        message: 'Activity shared successfully' 
      };
    }
  }

  async likeActivity(activityId) {
    try {
      const response = await authenticatedAPI.post(this.apiName, `/social/activity/${activityId}/like`);
      return response;
    } catch (error) {
      console.error('Error liking activity:', error);
      // Simulate success for demo purposes
      return { success: true, liked: true };
    }
  }

  async unlikeActivity(activityId) {
    try {
      const response = await authenticatedAPI.del(this.apiName, `/social/activity/${activityId}/like`);
      return response;
    } catch (error) {
      console.error('Error unliking activity:', error);
      // Simulate success for demo purposes
      return { success: true, liked: false };
    }
  }

  async commentOnActivity(activityId, comment) {
    try {
      const response = await authenticatedAPI.post(this.apiName, `/social/activity/${activityId}/comment`, {
        body: { comment }
      });
      return response;
    } catch (error) {
      console.error('Error commenting on activity:', error);
      // Simulate success for demo purposes
      return { 
        success: true, 
        comment: {
          id: 'comment_' + Date.now(),
          text: comment,
          user: { name: 'You' },
          timestamp: new Date().toISOString()
        }
      };
    }
  }

  async getActivityComments(activityId) {
    try {
      const response = await authenticatedAPI.get(this.apiName, `/social/activity/${activityId}/comments`);
      return response;
    } catch (error) {
      console.error('Error fetching comments:', error);
      // Return mock comments for demo purposes
      return {
        comments: [
          {
            id: 'comment1',
            text: 'Great job! Keep it up!',
            user: {
              id: 'user1',
              name: 'Alice Cooper',
              avatar: null
            },
            timestamp: '2024-01-20T15:00:00Z'
          },
          {
            id: 'comment2',
            text: 'Inspiring! 💪',
            user: {
              id: 'user2',
              name: 'Bob Wilson',
              avatar: null
            },
            timestamp: '2024-01-20T15:15:00Z'
          }
        ]
      };
    }
  }

  async getFriendLeaderboard(period = 'weekly') {
    try {
      const response = await authenticatedAPI.get(this.apiName, '/social/leaderboards', {
        queryStringParameters: { period }
      });
      return response;
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      // Return mock leaderboard for demo purposes
      return {
        leaderboard: [
          {
            rank: 1,
            user: {
              id: 'friend1',
              name: 'John Doe',
              avatar: null
            },
            steps: 85000,
            change: '+2'
          },
          {
            rank: 2,
            user: {
              id: 'friend2',
              name: 'Jane Smith',
              avatar: null
            },
            steps: 78000,
            change: '-1'
          },
          {
            rank: 3,
            user: {
              id: 'current',
              name: 'You',
              avatar: null
            },
            steps: 65000,
            change: '+1'
          }
        ],
        period: period,
        totalParticipants: 15
      };
    }
  }

  async getUserProfile(userId) {
    try {
      const response = await authenticatedAPI.get(this.apiName, `/users/${userId}/profile`);
      return response;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Return mock profile for demo purposes
      return {
        user: {
          id: userId,
          name: 'Demo User',
          email: 'demo@example.com',
          avatar: null,
          joinedDate: '2024-01-01T00:00:00Z',
          totalSteps: 250000,
          achievements: 5,
          currentStreak: 12,
          friendCount: 8
        }
      };
    }
  }

  async getUserActivities(userId, limit = 10) {
    try {
      const response = await authenticatedAPI.get(this.apiName, `/users/${userId}/activities`, {
        queryStringParameters: { limit: limit.toString() }
      });
      return response;
    } catch (error) {
      console.error('Error fetching user activities:', error);
      // Return mock activities for demo purposes
      return {
        activities: [
          {
            id: 'user_activity1',
            type: 'steps',
            description: 'Walked 12,000 steps',
            timestamp: '2024-01-20T10:00:00Z',
            data: { steps: 12000 }
          },
          {
            id: 'user_activity2',
            type: 'challenge',
            description: 'Joined "Weekend Warriors"',
            timestamp: '2024-01-19T15:30:00Z',
            data: { challengeName: 'Weekend Warriors' }
          }
        ]
      };
    }
  }
}

const socialApiService = new SocialApiService();
export default socialApiService;